import React, { useState } from 'react';
import { X, Check, CreditCard, Smartphone, CreditCard as KlarnaIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function PaymentModal({ isOpen, onClose }: PaymentModalProps) {
  const [swishNumber, setSwishNumber] = useState('');
  const [error, setError] = useState('');
  const [selectedPlan, setSelectedPlan] = useState<'premium' | 'standard' | 'day'>('standard');
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'swish' | 'klarna'>('swish');
  const [cardDetails, setCardDetails] = useState({
    number: '',
    name: '',
    expiry: '',
    cvc: ''
  });
  const navigate = useNavigate();

  if (!isOpen) return null;

  const getPlanPrice = () => {
    switch (selectedPlan) {
      case 'premium': return '499 kr';
      case 'standard': return '299 kr';
      case 'day': return '99 kr';
      default: return '299 kr';
    }
  };

  const handleSwishSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!swishNumber.match(/^07[0-9]{8}$/)) {
      setError('Vänligen ange ett giltigt mobilnummer (10 siffror)');
      return;
    }
    navigate('/payment-confirmation', { state: { swishNumber, plan: selectedPlan } });
    onClose();
  };

  const handleCardSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate('/payment-confirmation', { state: { cardDetails, plan: selectedPlan } });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full relative transform transition-all animate-fadeIn overflow-y-auto max-h-[90vh]">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="p-6">
          <h2 className="text-2xl font-semibold mb-4">Välj abonnemang</h2>
          
          {/* Plan Selection */}
          <div className="mb-6">
            <h3 className="font-semibold mb-4">Välj abonnemang</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Premium Plan */}
              <div
                className={`border rounded-lg p-4 cursor-pointer transition-all h-full ${
                  selectedPlan === 'premium'
                    ? 'border-[#F2C1D1] bg-[#F2C1D1]/10 shadow-md'
                    : 'border-gray-200 hover:border-[#F2C1D1]/50'
                }`}
                onClick={() => setSelectedPlan('premium')}
              >
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-semibold">Premium</h3>
                  <span className="text-[#F2C1D1] font-bold">499 kr/mån</span>
                </div>
                
                <div className="bg-gray-50 p-2 rounded-lg mb-2">
                  <p className="text-xs text-gray-600">Ansökningshjälpen AI och obegränsad djupanalys</p>
                </div>
                
                <ul className="text-xs space-y-1 mb-2">
                  <li className="flex items-start gap-1">
                    <Check className="w-3 h-3 text-[#F2C1D1] mt-0.5 flex-shrink-0" />
                    <span>Allt i Standard</span>
                  </li>
                  <li className="flex items-start gap-1">
                    <Check className="w-3 h-3 text-[#F2C1D1] mt-0.5 flex-shrink-0" />
                    <span>Obegränsad djupanalys</span>
                  </li>
                  <li className="flex items-start gap-1">
                    <Check className="w-3 h-3 text-[#F2C1D1] mt-0.5 flex-shrink-0" />
                    <span>Ansökningshjälpen AI</span>
                  </li>
                  <li className="flex items-start gap-1">
                    <Check className="w-3 h-3 text-[#F2C1D1] mt-0.5 flex-shrink-0" />
                    <span>Stiftelseaktivitet</span>
                  </li>
                </ul>
                
                <div className="w-full h-5 flex items-center">
                  {selectedPlan === 'premium' && (
                    <span className="text-[#F2C1D1] text-xs font-medium">✓ Valt</span>
                  )}
                </div>
              </div>
              
              {/* Standard Plan */}
              <div
                className={`border rounded-lg p-4 cursor-pointer transition-all relative h-full ${
                  selectedPlan === 'standard'
                    ? 'border-[#A3C1DA] bg-[#A3C1DA]/10 shadow-md'
                    : 'border-gray-200 hover:border-[#A3C1DA]/50'
                }`}
                onClick={() => setSelectedPlan('standard')}
              >
                <div className="absolute -top-2 right-2 bg-[#F2C1D1] text-white text-xs px-2 py-0.5 rounded">
                  Populärast
                </div>
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-semibold">Standard</h3>
                  <span className="text-[#A3C1DA] font-bold">299 kr/mån</span>
                </div>
                
                <div className="bg-gray-50 p-2 rounded-lg mb-2">
                  <p className="text-xs text-gray-600">Perfekt för regelbunden stiftelsesökning</p>
                </div>
                
                <ul className="text-xs space-y-1 mb-2">
                  <li className="flex items-start gap-1">
                    <Check className="w-3 h-3 text-[#A3C1DA] mt-0.5 flex-shrink-0" />
                    <span>AI-matchning</span>
                  </li>
                  <li className="flex items-start gap-1">
                    <Check className="w-3 h-3 text-[#A3C1DA] mt-0.5 flex-shrink-0" />
                    <span>Djupanalys (10/månad)</span>
                  </li>
                  <li className="flex items-start gap-1">
                    <Check className="w-3 h-3 text-[#A3C1DA] mt-0.5 flex-shrink-0" />
                    <span>E-post & webbinfo</span>
                  </li>
                  <li className="flex items-start gap-1">
                    <Check className="w-3 h-3 text-[#A3C1DA] mt-0.5 flex-shrink-0" />
                    <span>Stiftelseaktivitet</span>
                  </li>
                </ul>
                
                <div className="w-full h-5 flex items-center">
                  {selectedPlan === 'standard' && (
                    <span className="text-[#A3C1DA] text-xs font-medium">✓ Valt</span>
                  )}
                </div>
              </div>
              
              {/* Dagspass Plan */}
              <div
                className={`border rounded-lg p-4 cursor-pointer transition-all h-full ${
                  selectedPlan === 'day'
                    ? 'border-[#E6F0FA] bg-[#E6F0FA]/30 shadow-md'
                    : 'border-gray-200 hover:border-[#E6F0FA]'
                }`}
                onClick={() => setSelectedPlan('day')}
              >
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-semibold">Dagspass</h3>
                  <span className="text-gray-700 font-bold">99 kr</span>
                </div>
                
                <div className="bg-gray-50 p-2 rounded-lg mb-2">
                  <p className="text-xs text-gray-600">24 timmars tillgång till verktyg</p>
                </div>
                
                <ul className="text-xs space-y-1 mb-2">
                  <li className="flex items-start gap-1">
                    <Check className="w-3 h-3 text-gray-500 mt-0.5 flex-shrink-0" />
                    <span>Avancerad sökning</span>
                  </li>
                  <li className="flex items-start gap-1">
                    <Check className="w-3 h-3 text-gray-500 mt-0.5 flex-shrink-0" />
                    <span>Djupanalys (3 st)</span>
                  </li>
                  <li className="flex items-start gap-1">
                    <Check className="w-3 h-3 text-gray-500 mt-0.5 flex-shrink-0" />
                    <span>E-post & webbinfo</span>
                  </li>
                  <li className="flex items-start gap-1">
                    <Check className="w-3 h-3 text-gray-500 mt-0.5 flex-shrink-0" />
                    <span>Stiftelseaktivitet</span>
                  </li>
                </ul>
                
                <div className="w-full h-5 flex items-center">
                  {selectedPlan === 'day' && (
                    <span className="text-gray-500 text-xs font-medium">✓ Valt</span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="mt-4 bg-gray-50 p-3 rounded-lg">
              <h4 className="font-medium text-sm mb-2">Vad ingår i {selectedPlan === 'premium' ? 'Premium' : selectedPlan === 'standard' ? 'Standard' : 'Dagspass'}:</h4>
              <ul className="text-sm space-y-1">
                {selectedPlan === 'premium' && (
                  <>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#F2C1D1] mt-0.5 flex-shrink-0" />
                      <span>Ansökningshjälpen AI - perfekt formulerade ansökningar</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#F2C1D1] mt-0.5 flex-shrink-0" />
                      <span>Obegränsad djupanalys av stiftelser</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#F2C1D1] mt-0.5 flex-shrink-0" />
                      <span>Mallar för ansökningar anpassade till specifika stiftelser</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#F2C1D1] mt-0.5 flex-shrink-0" />
                      <span>Detaljerad information om stiftelsers aktivitet</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#F2C1D1] mt-0.5 flex-shrink-0" />
                      <span>Prioriterad support inom 24 timmar</span>
                    </li>
                  </>
                )}
                
                {selectedPlan === 'standard' && (
                  <>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#A3C1DA] mt-0.5 flex-shrink-0" />
                      <span>AI-driven matchning mot dina specifika behov</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#A3C1DA] mt-0.5 flex-shrink-0" />
                      <span>Djupanalys av stiftelser (10/månad)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#A3C1DA] mt-0.5 flex-shrink-0" />
                      <span>E-post & webbsideinformation för stiftelser</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#A3C1DA] mt-0.5 flex-shrink-0" />
                      <span>Information om stiftelsers aktivitet</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#A3C1DA] mt-0.5 flex-shrink-0" />
                      <span>Organisationsverktyg för ansökningar</span>
                    </li>
                  </>
                )}
                
                {selectedPlan === 'day' && (
                  <>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                      <span>Avancerad sökning i 24 timmar</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                      <span>Djupanalys av stiftelser (3 st)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                      <span>Tillgång till e-post & webbsideinformation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                      <span>Information om stiftelsers aktivitet</span>
                    </li>
                  </>
                )}
              </ul>
            </div>
          </div>
          
          {/* Payment Method Tabs */}
          <div className="mb-6">
            <h3 className="font-semibold mb-3">Välj betalningsmetod</h3>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => setPaymentMethod('swish')}
                className={`p-3 flex flex-col items-center gap-2 text-sm font-medium rounded-lg border ${
                  paymentMethod === 'swish'
                    ? 'border-[#A3C1DA] bg-[#A3C1DA]/10 text-[#A3C1DA]'
                    : 'border-gray-200 text-gray-500 hover:border-[#A3C1DA]/50'
                }`}
              >
                <Smartphone size={24} />
                <span>Swish</span>
              </button>
              <button
                onClick={() => setPaymentMethod('card')}
                className={`p-3 flex flex-col items-center gap-2 text-sm font-medium rounded-lg border ${
                  paymentMethod === 'card'
                    ? 'border-[#A3C1DA] bg-[#A3C1DA]/10 text-[#A3C1DA]'
                    : 'border-gray-200 text-gray-500 hover:border-[#A3C1DA]/50'
                }`}
              >
                <CreditCard size={24} />
                <span>Kreditkort</span>
              </button>
              <button
                onClick={() => setPaymentMethod('klarna')}
                className={`p-3 flex flex-col items-center gap-2 text-sm font-medium rounded-lg border ${
                  paymentMethod === 'klarna'
                    ? 'border-[#A3C1DA] bg-[#A3C1DA]/10 text-[#A3C1DA]'
                    : 'border-gray-200 text-gray-500 hover:border-[#A3C1DA]/50'
                }`}
              >
                <KlarnaIcon size={24} />
                <span>Klarna</span>
              </button>
            </div>
          </div>
          
          {/* Payment Forms */}
          {paymentMethod === 'swish' && (
            <form onSubmit={handleSwishSubmit} className="space-y-4">
              <div>
                <label htmlFor="swish" className="block text-sm font-medium text-gray-700">
                  Swish-nummer
                </label>
                <div className="flex items-center">
                  <span className="bg-gray-100 px-3 py-2 border border-gray-300 border-r-0 rounded-l-md text-gray-500">
                    +46
                  </span>
                  <input
                    type="tel"
                    id="swish"
                    value={swishNumber}
                    onChange={(e) => {
                      setSwishNumber(e.target.value.replace(/\D/g, ''));
                      setError('');
                    }}
                    placeholder="70XXXXXXX"
                    className="flex-grow px-3 py-2 border border-gray-300 rounded-r-md focus:outline-none focus:ring-2 focus:ring-[#A3C1DA]"
                    maxLength={10}
                  />
                </div>
                {error && (
                  <p className="mt-1 text-sm text-[#F2C1D1]">{error}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Inget belopp kommer att dras förrän du godkänner betalningen i Swish-appen
                </p>
              </div>
              <button
                type="submit"
                className="w-full bg-[#A3C1DA] text-white rounded-md py-3 px-4
                         hover:bg-[#7A98B0] transition-colors font-medium"
              >
                Betala {getPlanPrice()} med Swish
              </button>
            </form>
          )}
          
          {paymentMethod === 'card' && (
            <form onSubmit={handleCardSubmit} className="space-y-4">
              <div>
                <label htmlFor="cardNumber" className="block text-sm font-medium text-gray-700">
                  Kortnummer
                </label>
                <input
                  type="text"
                  id="cardNumber"
                  placeholder="1234 5678 9012 3456"
                  value={cardDetails.number}
                  onChange={(e) => setCardDetails({...cardDetails, number: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#A3C1DA]"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="cardName" className="block text-sm font-medium text-gray-700">
                  Namn på kortet
                </label>
                <input
                  type="text"
                  id="cardName"
                  placeholder="Anders Andersson"
                  value={cardDetails.name}
                  onChange={(e) => setCardDetails({...cardDetails, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#A3C1DA]"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="expiry" className="block text-sm font-medium text-gray-700">
                    Utgångsdatum
                  </label>
                  <input
                    type="text"
                    id="expiry"
                    placeholder="MM/ÅÅ"
                    value={cardDetails.expiry}
                    onChange={(e) => setCardDetails({...cardDetails, expiry: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#A3C1DA]"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="cvc" className="block text-sm font-medium text-gray-700">
                    CVC
                  </label>
                  <input
                    type="text"
                    id="cvc"
                    placeholder="123"
                    value={cardDetails.cvc}
                    onChange={(e) => setCardDetails({...cardDetails, cvc: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#A3C1DA]"
                    required
                  />
                </div>
              </div>
              
              <button
                type="submit"
                className="w-full bg-[#A3C1DA] text-white rounded-md py-3 px-4
                         hover:bg-[#7A98B0] transition-colors font-medium"
              >
                Betala {getPlanPrice()} med kort
              </button>
            </form>
          )}
          
          {paymentMethod === 'klarna' && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-3 mb-3">
                  <KlarnaIcon className="w-6 h-6 text-[#FFB3C7]" />
                  <h4 className="font-medium">Klarna</h4>
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  Betala med Klarna och få möjlighet att dela upp betalningen eller betala senare.
                </p>
                <ul className="text-sm space-y-1 text-gray-600">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>Betala direkt</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>Betala inom 14 dagar</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>Dela upp betalningen</span>
                  </li>
                </ul>
              </div>
              
              <button
                onClick={() => navigate('/payment-confirmation', { state: { paymentMethod: 'klarna', plan: selectedPlan } })}
                className="w-full bg-[#FFB3C7] text-white rounded-md py-3 px-4
                         hover:bg-[#FF9EB8] transition-colors font-medium"
              >
                Fortsätt till Klarna
              </button>
            </div>
          )}
          
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              Genom att fortsätta godkänner du våra <a href="#" className="text-[#A3C1DA]">användarvillkor</a> och <a href="#" className="text-[#A3C1DA]">integritetspolicy</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}